<template>
  <div>
    <!-- Header -->
    <div class="mb-4">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h4 class="mb-0 font-weight-bold text-dark">
            <i class="fa fa-user-edit mr-2 text-primary"></i>
            {{ enableEdit ? 'Editar Socio' : 'Detalle del Socio' }}
          </h4>
          <p class="text-muted mb-0">
            {{ enableEdit ? 'Modifique la información necesaria' : 'Visualizando información del socio' }}
          </p>
        </div>
        <div class="text-right">
          <small class="text-muted d-block">Estado actual</small>
          <span 
            class="badge badge-pill"
            :class="getEstadoBadgeClass(localSocio.estadoCuenta)"
          >
            {{ localSocio.estadoCuenta }}
          </span>
        </div>
      </div>
    </div>

    <card class="shadow-sm border-0">
      <div class="d-flex flex-column flex-md-row">
        <!-- Columna principal con datos del socio -->
        <div class="col-12 col-md-9">
          <!-- Sección móvil de avatar y controles -->
          <div class="d-flex d-md-none flex-column justify-content-center mb-4">
            <!-- Avatar Card -->
            <div class="card shadow-sm border-0 mb-3">
              <div class="card-body text-center py-4 position-relative">
                <template v-if="enableEdit">
                  <el-tooltip :open-delay="200" placement="top">
                    <template #content>Eliminar Socio</template>
                    <button
                      @click="$emit('delete-socio')"
                      class="btn btn-danger btn-sm rounded-circle position-absolute"
                      style="top: 10px; right: 10px; width: 35px; height: 35px;"
                    >
                      <i class="fa fa-trash"></i>
                    </button>
                  </el-tooltip>
                </template>
                
                <div
                  class="rounded-circle mx-auto overflow-hidden d-flex shadow-sm avatar-container"
                  :class="!avatarSrc ? 'bg-gradient-primary' : ''"
                >
                  <img
                    v-if="avatarSrc"
                    :src="avatarSrc"
                    alt="Imagen del socio"
                    class="w-100 h-100 object-fit-cover"
                  />
                  <i
                    v-else
                    class="fa fa-user text-white mx-auto my-auto avatar-icon"
                  ></i>
                </div>
                
                <button
                  class="btn btn-primary btn-sm rounded-pill mt-3"
                  @click="handleOpenAvatarModal"
                  :disabled="!enableEdit"
                >
                  <i class="fa fa-camera mr-2"></i>
                  {{ enableEdit ? 'Cambiar foto' : 'Ver foto' }}
                </button>
              </div>
            </div>
          
            <!-- Controles básicos móvil -->
            <div class="row">
              <div class="col-6">
                <div class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-building mr-1"></i>
                    Sucursal de origen
                  </label>
                  <el-select
                    v-model="localSocio.selectedOriginBranch"
                    placeholder="Seleccionar..."
                    class="w-100"
                    :disabled="!enableEdit"
                  >
                    <el-option
                      v-for="(item, index) in branches"
                      :key="index"
                      :label="item.nombreSucursal"
                      :value="JSON.stringify(item)"
                    ></el-option>
                  </el-select>
                </div>
              </div>
              
              <div class="col-6">
                <div class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-lock mr-1"></i>
                    Acceso a Sucursal <span class="text-danger">*</span>
                  </label>
                  <el-select
                    v-model="localSocio.accesoSucursal.estadoAcceso"
                    placeholder="Seleccionar..."
                    class="w-100"
                    :disabled="!enableEdit"
                    :class="{
                      'select-danger': !localSocio.accesoSucursal.estadoAcceso && enableEdit,
                    }"
                  >
                    <el-option
                      v-for="(item, index) in estadosAcceso"
                      :key="index"
                      :label="item.glosa"
                      :value="item.glosa"
                    >
                      <span class="d-flex align-items-center">
                        <i 
                          class="fa mr-2"
                          :class="item.glosa === 'AUTORIZADO' ? 'fa-check text-success' : 'fa-times text-danger'"
                        ></i>
                        {{ item.glosa }}
                      </span>
                    </el-option>
                  </el-select>
                  <small
                    v-if="!localSocio.accesoSucursal.estadoAcceso && enableEdit"
                    class="text-danger"
                  >Campo obligatorio</small>
                </div>
                
                <!-- Campo causal de bloqueo - Solo visible cuando NO AUTORIZADO -->
                <div v-if="localSocio.accesoSucursal.estadoAcceso === 'NO AUTORIZADO'" class="form-group mt-3">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-exclamation-triangle mr-1 text-warning"></i>
                    Causal de Bloqueo <span class="text-danger">*</span>
                  </label>
                  <textarea
                    class="form-control"
                    placeholder="Especifique la razón del bloqueo de acceso..."
                    v-model.trim="localSocio.accesoSucursal.causalBloqueo"
                    :disabled="!enableEdit"
                    :class="{ 'is-invalid': !localSocio.accesoSucursal.causalBloqueo && enableEdit && localSocio.accesoSucursal.estadoAcceso === 'NO AUTORIZADO' }"
                    :rows="2"
                  ></textarea>
                  <small v-if="!localSocio.accesoSucursal.causalBloqueo && enableEdit && localSocio.accesoSucursal.estadoAcceso === 'NO AUTORIZADO'" class="text-danger">
                    Es obligatorio indicar la razón del bloqueo
                  </small>
                </div>
              </div>
            </div>
          </div>

          <!-- Información de Socio -->
          <div class="card mb-4 shadow-sm border-0">
            <div class="card-header bg-light border-0">
              <h6 class="mb-0">
                <i class="fa fa-user mr-2 text-primary"></i>
                Información Personal
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-12 col-md-5">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-id-card mr-1"></i>
                      Nombre completo <span class="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Ingrese nombre completo"
                      v-model.trim="localSocio.nombreSocio"
                      :disabled="!enableEdit"
                      :class="{ 'is-invalid': !localSocio.nombreSocio && enableEdit }"
                    />
                    <small v-if="!localSocio.nombreSocio && enableEdit" class="text-danger">
                      Campo obligatorio
                    </small>
                  </div>
                </div>
          
                <el-tooltip :open-delay="200" placement="top" :disabled="true">
                  <template #content>
                    El RUT no puede ser modificado
                  </template>
                  <div class="col-6 col-md-3">
                    <div class="form-group">
                      <label class="form-label small text-secondary mb-1">
                        <i class="fa fa-fingerprint mr-1"></i>
                        RUT <span class="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        class="form-control"
                        placeholder="11111111-1"
                        v-model.trim="localSocio.rut"
                        :disabled="true"
                        :class="{ 'is-invalid': (!localSocio.rut || !rutValidator) && enableEdit }"
                      />
                      <small v-if="!localSocio.rut && enableEdit" class="text-danger">
                        Campo obligatorio
                      </small>
                      <small v-if="!rutValidator && localSocio.rut && enableEdit" class="text-danger">
                        RUT inválido
                      </small>
                    </div>
                  </div>
                </el-tooltip>
                
                <div class="col-6 col-md-2">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-venus-mars mr-1"></i>
                      Sexo
                    </label>
                    <el-select
                      v-model="localSocio.sexo"
                      placeholder="Seleccionar..."
                      class="w-100"
                      :disabled="!enableEdit"
                    >
                      <el-option label="HOMBRE" value="HOMBRE">
                        <span class="d-flex align-items-center">
                          <i class="fa fa-mars text-primary mr-2"></i>
                          Hombre
                        </span>
                      </el-option>
                      <el-option label="MUJER" value="MUJER">
                        <span class="d-flex align-items-center">
                          <i class="fa fa-venus text-danger mr-2"></i>
                          Mujer
                        </span>
                      </el-option>
                      <el-option label="NO ESPECIFICADO" value="">
                        <span class="d-flex align-items-center">
                          <i class="fa fa-genderless text-muted mr-2"></i>
                          No especificado
                        </span>
                      </el-option>
                    </el-select>
                  </div>
                </div>
                
                <div class="col-4 col-md-2">
                  <div class="form-group text-center">
                    <label class="form-label small text-secondary mb-1 d-block">
                      <i class="fa fa-globe mr-1"></i>
                      ¿Extranjero?
                    </label>
                    <p-switch
                      type="primary"
                      class="mx-auto"
                      on-text="SI"
                      off-text="NO"
                      :disabled="!enableEdit"
                      v-model="localSocio.esExtranjero"
                    ></p-switch>
                  </div>
                </div>
          
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-calendar mr-1"></i>
                      Fecha de nacimiento
                    </label>
                    <el-date-picker
                      type="date"
                      class="w-100"
                      placeholder="Seleccionar fecha"
                      format="dd-MM-yyyy"
                      :disabled="!enableEdit"
                      value-format="dd-MM-yyyy"
                      v-model="localSocio.informacionAdicional.fechaNacimiento"
                    >
                    </el-date-picker>
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-user-tag mr-1"></i>
                      Perfil <span class="text-danger">*</span>
                    </label>
                    <el-select
                      placeholder="Seleccionar perfil"
                      v-model="localSocio.perfilCuenta"
                      class="w-100"
                      :disabled="!enableEdit"
                      :class="{ 'select-danger': !localSocio.perfilCuenta && enableEdit }"
                    >
                      <el-option
                        v-for="(item, index) in perfiles"
                        :key="index"
                        :label="item.glosa"
                        :value="item.glosa"
                      >
                      </el-option>
                    </el-select>
                    <small v-if="!localSocio.perfilCuenta && enableEdit" class="text-danger">
                      Campo obligatorio
                    </small>
                  </div>
                </div>

                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-info-circle mr-1"></i>
                      Estado <span class="text-danger">*</span>
                    </label>
                    <el-select
                      placeholder="Seleccionar estado"
                      v-model="localSocio.estadoCuenta"
                      class="w-100"
                      :disabled="!enableEdit"
                      :class="{ 'select-danger': !localSocio.estadoCuenta && enableEdit }"
                    >
                      <el-option
                        v-for="(item, index) in estados"
                        :key="index"
                        :label="item.glosa"
                        :value="item.glosa"
                      >
                        <span class="d-flex align-items-center">
                          <span 
                            class="badge mr-2"
                            :class="getEstadoBadgeClass(item.glosa)"
                          >
                            &nbsp;
                          </span>
                          {{ item.glosa }}
                        </span>
                      </el-option>
                    </el-select>
                    <small v-if="!localSocio.estadoCuenta && enableEdit" class="text-danger">
                      Campo obligatorio
                    </small>
                  </div>
                </div>
          
                <div class="col-12 d-md-none" v-if="esPerfilCarga">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-user-tie mr-1"></i>
                      RUT Titular Convenio
                    </label>
                    <input
                      type="text"
                      placeholder="11111111-1"
                      class="form-control"
                      v-model.trim="localSocio.informacionAdicional.rutTitularConvenio"
                      :disabled="!enableEdit"
                    />
                    <small
                      v-if="
                        localSocio.informacionAdicional.rutTitularConvenio &&
                        !validateRut(localSocio.informacionAdicional.rutTitularConvenio)
                      "
                      class="text-danger"
                    >RUT inválido</small>
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group text-center">
                    <label class="form-label small text-secondary mb-1 d-block">
                      <i class="fa fa-bell mr-1"></i>
                      Notificar vencimientos
                    </label>
                    <el-tooltip
                      :open-delay="200"
                      placement="top"
                    >
                      <template #content>
                        Notifica el vencimiento de<br />las membresías por correo
                      </template>
                      <p-switch
                        type="success"
                        class="mx-auto mb-0"
                        on-text="SI"
                        off-text="NO"
                        :disabled="!enableEdit"
                        v-model="localSocio.informacionAdicional.notificarVencimiento"
                      ></p-switch>
                    </el-tooltip>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Información de Contacto -->
          <div class="card mb-4 shadow-sm border-0">
            <div class="card-header bg-light border-0">
              <h6 class="mb-0">
                <i class="fa fa-envelope mr-2 text-primary"></i>
                Información de Contacto
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-at mr-1"></i>
                      Email principal <span class="text-danger">*</span>
                    </label>
                    <input
                      type="email"
                      class="form-control"
                      placeholder="correo@ejemplo.com"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.autenticacion.email"
                      :class="{
                        'is-invalid': (!localSocio.autenticacion.email || !emailValidator) && enableEdit,
                      }"
                    />
                    <small v-if="!localSocio.autenticacion.email && enableEdit" class="text-danger">
                      Campo obligatorio
                    </small>
                    <small
                      v-if="!emailValidator && localSocio.autenticacion.email && enableEdit"
                      class="text-danger"
                    >
                      Email inválido
                    </small>
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-envelope-o mr-1"></i>
                      Email secundario
                    </label>
                    <input
                      type="email"
                      class="form-control"
                      placeholder="correo2@ejemplo.com"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionContacto.emailSecundario"
                    />
                  </div>
                </div>

                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-phone mr-1"></i>
                      Teléfono
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="+56 2 1234 5678"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionContacto.telefonoFijo"
                    />
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-mobile mr-1"></i>
                      Celular
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="+56 9 1234 5678"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionContacto.celular"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Información Geográfica -->
          <div class="card mb-4 shadow-sm border-0">
            <div class="card-header bg-light border-0">
              <h6 class="mb-0">
                <i class="fa fa-map-marker-alt mr-2 text-primary"></i>
                Información Geográfica
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-4 col-md-2">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-flag mr-1"></i>
                      País
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Chile"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionGeografica.pais"
                    />
                  </div>
                </div>
                
                <div class="col-4 col-md-2">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-city mr-1"></i>
                      Ciudad
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Santiago"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionGeografica.ciudad"
                    />
                  </div>
                </div>
                
                <div class="col-4 col-md-2">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-map mr-1"></i>
                      Región
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Metropolitana"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionGeografica.region"
                    />
                  </div>
                </div>
                
                <div class="col-4 col-md-2">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-home mr-1"></i>
                      Comuna
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Las Condes"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionGeografica.comuna"
                    />
                  </div>
                </div>
                
                <div class="col-8 col-md-4">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fa fa-road mr-1"></i>
                      Dirección
                    </label>
                    <input
                      type="text"
                      class="form-control"
                      placeholder="Av. Principal 123"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionGeografica.direccion"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Redes Sociales -->
          <div class="card mb-4 shadow-sm border-0">
            <div class="card-header bg-light border-0">
              <h6 class="mb-0">
                <i class="fa fa-share-alt mr-2 text-primary"></i>
                Redes Sociales
              </h6>
            </div>
            <div class="card-body">
              <div class="row">
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fab fa-facebook-square mr-1" style="color: #4267b2"></i>
                      Facebook
                    </label>
                    <input
                      type="text"
                      placeholder="usuario.facebook"
                      class="form-control"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionContacto.facebook"
                    />
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fab fa-twitter-square mr-1" style="color: #1da1f2"></i>
                      Twitter
                    </label>
                    <input
                      type="text"
                      placeholder="@usuario"
                      class="form-control"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionContacto.twitter"
                    />
                  </div>
                </div>

                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fab fa-instagram mr-1" style="color: #f56040"></i>
                      Instagram
                    </label>
                    <input
                      type="text"
                      placeholder="@usuario"
                      class="form-control"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionContacto.instagram"
                    />
                  </div>
                </div>
                
                <div class="col-6 col-md-3">
                  <div class="form-group">
                    <label class="form-label small text-secondary mb-1">
                      <i class="fab fa-linkedin mr-1" style="color: #0e76a8"></i>
                      LinkedIn
                    </label>
                    <input
                      type="text"
                      placeholder="usuario-linkedin"
                      class="form-control"
                      :disabled="!enableEdit"
                      v-model.trim="localSocio.informacionContacto.linkedin"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Botones de acción -->
          <div class="d-flex justify-content-center mt-5 mb-3">
            <button
              class="btn btn-primary btn-lg px-5 mr-3"
              v-if="!enableEdit"
              @click="handleEnableEdit"
            >
              <i class="fa fa-edit mr-2"></i>
              Editar Información
            </button>
            
            <el-tooltip
              :open-delay="200"
              placement="top"
              :disabled="isValidForm && hasChanges"
              v-else
            >
              <template #content>
                <div class="text-center">
                  <i v-if="!hasChanges" class="fa fa-info-circle mb-2 fa-2x"></i>
                  <i v-else class="fa fa-exclamation-triangle mb-2 fa-2x"></i>
                  <p class="mb-0">
                    {{
                      !hasChanges
                        ? 'No hay cambios que guardar'
                        : 'Completa todos los campos obligatorios'
                    }}
                  </p>
                </div>
              </template>
              <button
                class="btn btn-success btn-lg px-5 mr-3"
                :disabled="!isValidForm || !hasChanges"
                @click="handleSave"
              >
                <i class="fa fa-save mr-2"></i>
                Guardar Cambios
              </button>
            </el-tooltip>
            
            <button
              class="btn btn-outline-secondary btn-lg px-5"
              @click="handleCancel"
            >
              <i class="fa fa-times mr-2"></i>
              {{ hasChanges && enableEdit ? "Cancelar" : "Cerrar" }}
            </button>
          </div>
        </div>

        <!-- Columna lateral con avatar y controles (desktop) -->
        <div class="col-12 col-md-3 d-none d-md-block">
          <div class="sticky-top" style="top: 20px;">
            <!-- Avatar Card -->
            <div class="card shadow-sm border-0 mb-4">
              <div class="card-body text-center py-4">
                <div
                  class="rounded-circle mx-auto overflow-hidden d-flex shadow-sm avatar-container-lg"
                  :class="!avatarSrc ? 'bg-gradient-primary' : ''"
                >
                  <img
                    v-if="avatarSrc"
                    :src="avatarSrc"
                    alt="Imagen del socio"
                    class="w-100 h-100 object-fit-cover"
                  />
                  <i
                    v-else
                    class="fa fa-user text-white mx-auto my-auto avatar-icon-lg"
                  ></i>
                </div>
                
                <button
                  class="btn btn-primary btn-sm rounded-pill mt-3"
                  @click="handleOpenAvatarModal"
                  :disabled="!enableEdit"
                >
                  <i class="fa fa-camera mr-2"></i>
                  {{ enableEdit ? 'Cambiar foto' : 'Ver foto' }}
                </button>
              </div>
            </div>

            <!-- Controles Adicionales -->
            <div class="card shadow-sm border-0">
              <div class="card-header bg-light border-0">
                <h6 class="mb-0">
                  <i class="fa fa-cog mr-2 text-primary"></i>
                  Configuración
                </h6>
              </div>
              <div class="card-body">
        
                <div v-if="esPerfilCarga" class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-user-tie mr-1"></i>
                    RUT Titular Convenio
                  </label>
                  <input
                    type="text"
                    placeholder="11111111-1"
                    class="form-control form-control-sm"
                    v-model.trim="localSocio.informacionAdicional.rutTitularConvenio"
                    :disabled="!enableEdit"
                  />
                  <small
                    v-if="
                      localSocio.informacionAdicional.rutTitularConvenio &&
                      !validateRut(localSocio.informacionAdicional.rutTitularConvenio)
                    "
                    class="text-danger"
                  >RUT inválido</small>
                </div>
                
                <div class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-building mr-1"></i>
                    Sucursal de origen
                  </label>
                  <el-select
                    v-model="localSocio.selectedOriginBranch"
                    placeholder="Seleccionar..."
                    class="w-100"
                    :disabled="!enableEdit"
                  >
                    <el-option
                      v-for="(item, index) in branches"
                      :key="index"
                      :label="item.nombreSucursal"
                      :value="JSON.stringify(item)"
                    ></el-option>
                  </el-select>
                </div>
                
                <div class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-lock mr-1"></i>
                    Acceso a Sucursal <span class="text-danger">*</span>
                  </label>
                  <el-select
                    v-model="localSocio.accesoSucursal.estadoAcceso"
                    placeholder="Seleccionar..."
                    class="w-100"
                    :disabled="!enableEdit"
                    :class="{
                      'select-danger': !localSocio.accesoSucursal.estadoAcceso && enableEdit,
                    }"
                  >
                    <el-option
                      v-for="(item, index) in estadosAcceso"
                      :key="index"
                      :label="item.glosa"
                      :value="item.glosa"
                    >
                      <span class="d-flex align-items-center">
                        <i 
                          class="fa mr-2"
                          :class="item.glosa === 'AUTORIZADO' ? 'fa-check text-success' : 'fa-times text-danger'"
                        ></i>
                        {{ item.glosa }}
                      </span>
                    </el-option>
                  </el-select>
                  <small
                    v-if="!localSocio.accesoSucursal.estadoAcceso && enableEdit"
                    class="text-danger"
                  >Campo obligatorio</small>
                </div>
                
                <!-- Campo causal de bloqueo - Solo visible cuando NO AUTORIZADO -->
                <div v-if="localSocio.accesoSucursal.estadoAcceso === 'NO AUTORIZADO'" class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-exclamation-triangle mr-1 text-warning"></i>
                    Causal de Bloqueo <span class="text-danger">*</span>
                  </label>
                  <textarea
                    class="form-control form-control-sm"
                    placeholder="Especifique la razón del bloqueo..."
                    v-model.trim="localSocio.accesoSucursal.causalBloqueo"
                    :disabled="!enableEdit"
                    :class="{ 'is-invalid': !localSocio.accesoSucursal.causalBloqueo && enableEdit && localSocio.accesoSucursal.estadoAcceso === 'NO AUTORIZADO' }"
                    :rows="2"
                  ></textarea>
                  <small v-if="!localSocio.accesoSucursal.causalBloqueo && enableEdit && localSocio.accesoSucursal.estadoAcceso === 'NO AUTORIZADO'" class="text-danger">
                    Debe indicar la razón
                  </small>
                </div>

                <div class="form-group">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-file-contract mr-1"></i>
                    Número de Contrato
                  </label>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    placeholder="Ej: CTR-2024-001"
                    v-model.trim="localSocio.informacionAdicional.contrato"
                    :disabled="!enableEdit"
                  />
                </div>

                <div class="form-group mb-0">
                  <label class="form-label small text-secondary mb-1">
                    <i class="fa fa-comment-alt mr-1"></i>
                    Comentarios / Notas
                  </label>
                  <textarea
                    class="form-control form-control-sm"
                    placeholder="Información adicional del socio..."
                    v-model="localSocio.informacionAdicional.detalleFicha"
                    :disabled="!enableEdit"
                    :rows="3"
                  ></textarea>
                </div>
        
              </div>
            </div>

            <!-- Acciones Rápidas -->
            <div class="mt-3" v-if="enableEdit">
              <div class="d-flex justify-content-center">
                <el-tooltip :open-delay="200" placement="top">
                  <template #content>
                    {{ tieneMembresiaAsignada ? 'Ver Membresía' : 'Asignar Membresía' }}
                  </template>
                  <button
                    class="btn btn-sm rounded-circle mr-2"
                    :class="tieneMembresiaAsignada ? 'btn-success' : 'btn-primary'"
                    @click="$emit('go-to-memberships')"
                    style="width: 45px; height: 45px;"
                  >
                    <i
                      class="fa"
                      :class="tieneMembresiaAsignada ? 'fa-search' : 'fa-plus'"
                    ></i>
                  </button>
                </el-tooltip>
                
                <el-tooltip :open-delay="200" placement="top">
                  <template #content>Eliminar Socio</template>
                  <button
                    @click="$emit('delete-socio')"
                    class="btn btn-danger btn-sm rounded-circle"
                    style="width: 45px; height: 45px;"
                  >
                    <i class="fa fa-trash"></i>
                  </button>
                </el-tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>
    </card>
  </div>
</template>

<script>
import { Tooltip, Select, Option, DatePicker } from 'element-ui';
import PSwitch from 'src/components/UIComponents/Switch.vue';
import _ from 'lodash';

export default {
  name: 'SocioDetalle',
  components: {
    [Tooltip.name]: Tooltip,
    [Select.name]: Select,
    [Option.name]: Option,
    [DatePicker.name]: DatePicker,
    PSwitch
  },
  props: {
    socio: {
      type: Object,
      required: true
    },
    branches: {
      type: Array,
      default: () => []
    },
    estados: {
      type: Array,
      default: () => []
    },
    estadosAcceso: {
      type: Array,
      default: () => []
    },
    perfiles: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      localSocio: {},
      originalSocio: {},
      enableEdit: false,
      avatarHasChanged: false,
      avatarBeforeModal: null
    };
  },
  computed: {
    esPerfilCarga() {
      return this.localSocio.perfilCuenta === 'CARGA';
    },
    
    avatarSrc() {
      if (this.localSocio.avatar?.imagenPerfil) {
        return `data:image/jpg;base64,${this.localSocio.avatar.imagenPerfil}`;
      }
      return null;
    },
    
    tieneMembresiaAsignada() {
      return !!this.localSocio.planes?.principal;
    },
    
    isValidForm() {
      const baseValidation = (
        this.localSocio.nombreSocio &&
        this.localSocio.rut &&
        this.localSocio.perfilCuenta &&
        this.localSocio.estadoCuenta &&
        this.localSocio.autenticacion?.email &&
        this.localSocio.accesoSucursal?.estadoAcceso &&
        this.localSocio.selectedOriginBranch &&
        this.rutValidator &&
        this.emailValidator
      );
      
      // Si el acceso es NO AUTORIZADO, también debe tener causal de bloqueo
      if (this.localSocio.accesoSucursal?.estadoAcceso === 'NO AUTORIZADO') {
        return baseValidation && !!this.localSocio.accesoSucursal?.causalBloqueo;
      }
      
      return baseValidation;
    },
    
    rutValidator() {
      if (this.localSocio.esExtranjero) {
        return true;
      }
      return this.validateRut(this.localSocio.rut);
    },
    
    emailValidator() {
      const regex = /^[a-zA-Z0-9._&-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,3}$/;
      return regex.test(this.localSocio.autenticacion?.email || '');
    },
    
    hasChanges() {
      if (!this.enableEdit) return false;
      
      // Primero verificar si el avatar ha cambiado
      if (this.avatarHasChanged) {
        return true;
      }
      
      // Comparación profunda de los objetos
      const hasAnyChange = !_.isEqual(this.localSocio, this.originalSocio);
      
      return hasAnyChange;
    }
  },
  watch: {
    socio: {
      immediate: true,
      deep: true,
      handler(newVal, oldVal) {
        if (newVal) {
          // Primera carga - copiar todo
          if (!this.localSocio._id) {
            this.localSocio = _.cloneDeep(newVal);
            this.originalSocio = _.cloneDeep(newVal);
            return;
          }
          
          // Detectar cambio de avatar
          if (this.enableEdit && newVal && newVal.avatar) {
            // Comparar avatares
            let avatarChanged = false;
            
            if (!oldVal || !oldVal.avatar) {
              // Si no había avatar antes pero ahora sí
              avatarChanged = true;
            } else if (oldVal.avatar.imagenPerfil !== newVal.avatar.imagenPerfil ||
                       oldVal.avatar.imagenMiniatura !== newVal.avatar.imagenMiniatura) {
              // Si cambió alguna de las imágenes
              avatarChanged = true;
            }
            
            if (avatarChanged) {
              // En modo edición, solo actualizar el avatar en localSocio
              // Crear un nuevo objeto para asegurar reactividad
              this.localSocio = {
                ...this.localSocio,
                avatar: _.cloneDeep(newVal.avatar)
              };
              // Marcar que el avatar ha cambiado
              this.avatarHasChanged = true;
              // Forzar actualización del componente
              this.$forceUpdate();
            }
          } else if (!this.enableEdit && newVal) {
            // Si no estamos en modo edición, actualizar todo
            this.localSocio = _.cloneDeep(newVal);
            this.originalSocio = _.cloneDeep(newVal);
            // Resetear el flag porque no estamos editando
            this.avatarHasChanged = false;
          }
        }
      }
    },
    
    'localSocio.accesoSucursal.estadoAcceso': function(newVal) {
      // Si cambia a AUTORIZADO, limpiar la causal de bloqueo
      if (newVal === 'AUTORIZADO' && this.localSocio.accesoSucursal) {
        this.localSocio.accesoSucursal.causalBloqueo = '';
      }
    }
  },
  methods: {
    validateRut(rut) {
      if (!rut) return false;
      
      const cleanRut = rut.trim();
      const regex = /^\d{7,8}-[\dkK]$/;
      
      if (!regex.test(cleanRut)) {
        return false;
      }
      
      const parts = cleanRut.split('-');
      const cuerpo = parts[0];
      const dv = parts[1].toUpperCase();
      
      let suma = 0;
      let multiplo = 2;
      
      for (let i = cuerpo.length - 1; i >= 0; i--) {
        suma += multiplo * cuerpo.charAt(i);
        multiplo = multiplo === 7 ? 2 : multiplo + 1;
      }
      
      let dvEsperado = 11 - (suma % 11);
      if (dvEsperado === 11) {
        dvEsperado = '0';
      } else if (dvEsperado === 10) {
        dvEsperado = 'K';
      } else {
        dvEsperado = dvEsperado.toString();
      }
      
      return dv === dvEsperado;
    },
    
    getEstadoBadgeClass(estado) {
      switch (estado) {
        case 'ACTIVA':
          return 'badge-success';
        case 'PENDIENTE':
          return 'badge-warning';
        case 'INACTIVA':
          return 'badge-secondary';
        case 'BLOQUEADA':
          return 'badge-danger';
        case 'ELIMINADA':
          return 'badge-dark';
        default:
          return 'badge-secondary';
      }
    },
    
    handleEnableEdit() {
      this.originalSocio = _.cloneDeep(this.localSocio);
      this.enableEdit = true;
      this.avatarHasChanged = false;
    },
    
    handleSave() {
      this.$emit('save', this.localSocio);
      this.avatarHasChanged = false;
    },
    
    handleCancel() {
      if (this.hasChanges && this.enableEdit) {
        this.enableEdit = false;
        this.localSocio = _.cloneDeep(this.originalSocio);
        this.originalSocio = {};
        this.avatarHasChanged = false;
      } else {
        this.$emit('close');
      }
    },
    
    handleOpenAvatarModal() {
      this.$emit('open-avatar-modal');
    },
    
    handleAvatarUpdate(newAvatar) {
      if (this.enableEdit && newAvatar) {
        // Actualizar el avatar local
        this.localSocio = {
          ...this.localSocio,
          avatar: {
            imagenMiniatura: newAvatar.imagenMiniatura,
            imagenPerfil: newAvatar.imagenPerfil
          }
        };
        // Marcar que ha cambiado
        this.avatarHasChanged = true;
        this.$forceUpdate();
      }
    }
  }
};
</script>

<style scoped>
/* Avatares */
.avatar-container {
  width: 120px;
  height: 120px;
}

.avatar-container-lg {
  width: 150px;
  height: 150px;
}

.avatar-icon {
  font-size: 60px;
}

.avatar-icon-lg {
  font-size: 80px;
}

/* Gradiente para avatares */
.bg-gradient-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

/* Object fit */
.object-fit-cover {
  object-fit: cover;
}

/* Select con error */
.select-danger .el-input__inner {
  border-color: #dc3545 !important;
}

/* Cards mejoradas */
.card {
  transition: all 0.3s ease;
}

.card:hover {
  transform: translateY(-2px);
}

/* Form labels */
.form-label {
  font-weight: 500;
  color: #495057;
}

/* Form groups mejorados */
.form-group {
  margin-bottom: 1rem;
}

/* Input groups mejorados */
.form-control:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

/* Inputs deshabilitados */
.form-control:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

/* Badge mejorados */
.badge {
  padding: 0.25em 0.5em;
  font-weight: 500;
}

/* Botones mejorados */
.btn-lg {
  font-weight: 500;
  letter-spacing: 0.5px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #5a67d8 0%, #68428e 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.btn-primary:disabled {
  background: #ccc;
  cursor: not-allowed;
}

.btn-success {
  background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
  border: none;
}

.btn-success:hover {
  background: linear-gradient(135deg, #38a169 0%, #2f855a 100%);
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

/* Sticky sidebar */
.sticky-top {
  position: sticky;
}

/* Select personalizado */
.el-select {
  width: 100%;
}

.el-select >>> .el-input__inner {
  border-radius: 0.25rem;
  border-color: #ced4da;
  font-size: 0.875rem;
}

.el-select >>> .el-input__inner:focus {
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0,123,255,.25);
}

.el-select >>> .el-input__inner:disabled {
  background-color: #e9ecef;
  cursor: not-allowed;
}

/* Card header mejorado */
.card-header {
  background-color: #f8f9fa !important;
}

/* Shadow suave */
.shadow-sm {
  box-shadow: 0 0.125rem 0.25rem rgba(0,0,0,0.075) !important;
}

/* Posición absoluta para botón de eliminar */
.position-absolute {
  position: absolute;
}

/* Transiciones */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Responsividad */
@media (max-width: 768px) {
  .avatar-container {
    width: 100px;
    height: 100px;
  }
  
  .avatar-icon {
    font-size: 50px;
  }
  
  .btn-lg {
    padding: 0.375rem 1rem;
    font-size: 0.875rem;
  }
}
</style>
